import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { API_ORIGIN, complaintAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminComplaints.css';

function getComplaintMedia(complaint) {
  if (!complaint) return [];
  let media = complaint.media_urls || [];
  if (typeof media === 'string') {
    try { media = JSON.parse(media); } catch { media = []; }
  }
  const list = Array.isArray(media) ? [...media] : [];
  if (!list.length && complaint.photo_before_url) {
    list.push({ url: complaint.photo_before_url, type: 'image', originalName: 'तक्रार फोटो' });
  }
  if (complaint.photo_after_url) {
    list.push({ url: complaint.photo_after_url, type: 'image', originalName: 'निराकरणानंतरचा फोटो' });
  }
  return list;
}

function mediaUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

export default function AdminComplaints() {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'complaints', user);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState(null);
  const [complaintHistory, setComplaintHistory] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  const [exporting, setExporting] = useState(false);

  useBackButtonClose(!!statusModal, () => { setStatusModal(null); setComplaintHistory([]); });
  const [statusFilter, setStatusFilter] = useState('');

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const params = { page, limit: pageSize, sort_by: sort.key, sort_order: sort.direction };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (severityFilter) params.severity = severityFilter;
      if (search) params.search = search;
      const res = await complaintAPI.getAll(slug, params);
      setComplaints(res.data.complaints || []);
      setTotalPages(res.data.pagination?.totalPages || res.data.totalPages || 1);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) { setError('डेटा लोड करण्यात अयशस्वी'); }
    finally { setLoading(false); }
  }, [slug, page, pageSize, statusFilter, categoryFilter, severityFilter, search, sort]);

  useEffect(() => { loadData(); }, [loadData]);

  // Status-based allowed transitions
  const getStatusActions = (currentStatus) => {
    const transitions = {
      filed:        [{ status: 'acknowledged', label: 'स्वीकृत करा', cls: 'btn-primary' }, { status: 'assigned', label: 'नेमणूक करा', cls: 'btn-primary' }, { status: 'rejected', label: 'नाकारा', cls: 'btn-danger' }],
      acknowledged: [{ status: 'assigned', label: 'नेमणूक करा', cls: 'btn-primary' }, { status: 'rejected', label: 'नाकारा', cls: 'btn-danger' }],
      assigned:     [{ status: 'in_progress', label: 'प्रगतीपथावर', cls: 'btn-primary' }, { status: 'rejected', label: 'नाकारा', cls: 'btn-danger' }],
      in_progress:  [{ status: 'resolved', label: 'निराकरण करा', cls: 'btn-success' }, { status: 'rejected', label: 'नाकारा', cls: 'btn-danger' }],
      resolved:     [{ status: 'verified', label: 'सत्यापित करा', cls: 'btn-success' }, { status: 'closed', label: 'बंद करा', cls: 'btn-outline' }, { status: 'reopened', label: 'पुन्हा उघडा', cls: 'btn-warning' }],
      verified:     [{ status: 'closed', label: 'बंद करा', cls: 'btn-outline' }],
      closed:       [{ status: 'reopened', label: 'पुन्हा उघडा', cls: 'btn-warning' }],
      reopened:     [{ status: 'assigned', label: 'नेमणूक करा', cls: 'btn-primary' }, { status: 'in_progress', label: 'प्रगतीपथावर', cls: 'btn-primary' }],
      rejected:     [{ status: 'reopened', label: 'पुन्हा उघडा', cls: 'btn-warning' }],
    };
    return transitions[currentStatus] || [];
  };

  const handleStatusUpdate = async (status) => {
    setError(''); setSuccess('');
    try {
      await complaintAPI.updateStatus(slug, statusModal.id, {
        status,
        resolution_notes: statusModal.notes || '',
        expected_resolution_date: statusModal.expected_resolution_date || null
      });
      setSuccess(`तक्रार ${status}`);
      setStatusModal(null);
      setComplaintHistory([]);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'अद्ययावत करण्यात अयशस्वी'); }
  };

  const sortedComplaints = [...complaints].sort((a, b) => {
    const left = sort.key === 'created_at' ? new Date(a.created_at || a.createdAt || 0).getTime() : String(a[sort.key] || '').toLowerCase();
    const right = sort.key === 'created_at' ? new Date(b.created_at || b.createdAt || 0).getTime() : String(b[sort.key] || '').toLowerCase();
    return (left > right ? 1 : left < right ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
  });

  const changeSort = (key) => {
    setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const SortHeader = ({ column, children }) => (
    <button className={`complaint-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>
      {children}<span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { limit: Math.max(total, 500), page: 1, sort_by: sort.key, sort_order: sort.direction };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (severityFilter) params.severity = severityFilter;
      const response = await complaintAPI.getAll(slug, params);
      const rows = response.data.complaints || [];
      const escape = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [['UID', 'Title', 'Category', 'Severity', 'Status', 'Filed by', 'Date'], ...rows.map(c => [c.complaint_uid, c.title, c.category, c.severity, c.status, c.filedByUser?.full_name, new Date(c.created_at || c.createdAt).toLocaleDateString('en-IN')])].map(row => row.map(escape).join(',')).join('\n');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' }));
      link.download = `complaints-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) { setError('तक्रार निर्यात करण्यात अयशस्वी'); }
    finally { setExporting(false); }
  };

  return (
    <div>
      <div className="admin-card-header">
        <h2>📢 तक्रार व्यवस्थापन</h2>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {statusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) { setStatusModal(null); setComplaintHistory([]); } }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '95%', maxWidth: 720, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)', padding: '20px 28px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontFamily: 'monospace', background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '3px 10px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 700 }}>{statusModal.complaint_uid}</span>
                  <span style={{ background: { low: '#4caf50', medium: '#ff9800', high: '#f44336', critical: '#b71c1c' }[statusModal.severity] || '#888', color: '#fff', padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>{statusModal.severity}</span>
                  <span style={{ background: { filed: '#42a5f5', acknowledged: '#29b6f6', assigned: '#ffa726', in_progress: '#ffca28', resolved: '#66bb6a', verified: '#26a69a', closed: '#90a4ae', reopened: '#ef5350', rejected: '#e53935' }[statusModal.status] || '#888', color: '#fff', padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>{statusModal.status?.replace(/_/g, ' ')}</span>
                </div>
                <h2 style={{ margin: 0, color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{statusModal.title}</h2>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', marginTop: 4 }}>
                  {statusModal.category?.replace(/_/g, ' ')} &bull; दाखल {new Date(statusModal.createdAt || statusModal.created_at).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <button onClick={() => { setStatusModal(null); setComplaintHistory([]); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: '50%', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
            </div>

            <div style={{ padding: '22px 28px' }}>
              {/* Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', marginBottom: 22 }}>
                <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>तक्रारदार</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>{statusModal.filedByUser?.full_name || '—'}</div>
                  {statusModal.filedByUser?.phone && <div style={{ fontSize: '0.78rem', color: '#666' }}>📞 {statusModal.filedByUser.phone}</div>}
                </div>
                <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>स्थळ</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>{statusModal.location_address || '—'}</div>
                  {statusModal.ward_number && <div style={{ fontSize: '0.78rem', color: '#666' }}>प्रभाग: {statusModal.ward_number}</div>}
                </div>
                <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>नेमणूक</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>{statusModal.assignedOfficer?.full_name || statusModal.assigned_department || 'नेमणूक नाही'}</div>
                </div>
                <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 3 }}>अपेक्षित निराकरण</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
                    {statusModal.expected_resolution_date ? new Date(statusModal.expected_resolution_date).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                  {statusModal.resolved_at && <div style={{ fontSize: '0.78rem', color: '#27ae60' }}>✅ निराकरण: {new Date(statusModal.resolved_at).toLocaleDateString('mr-IN')}</div>}
                </div>
              </div>

              {/* Description */}
              <div style={{ background: '#fafafa', borderRadius: 10, padding: '14px 18px', marginBottom: 22, border: '1px solid #eee' }}>
                <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>वर्णन</div>
                <div style={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>{statusModal.description}</div>
              </div>

              {/* Attachments / Evidence */}
              {getComplaintMedia(statusModal).length > 0 && (
                <div style={{ background: '#fdfdff', borderRadius: 10, padding: '14px 18px', marginBottom: 22, border: '1px solid #e0e8f2' }}>
                  <div style={{ fontSize: '0.75rem', color: '#1a237e', textTransform: 'uppercase', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    📎 जोडलेले पुरावे ({getComplaintMedia(statusModal).length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                    {getComplaintMedia(statusModal).map((item, idx) => {
                      const url = typeof item === 'string' ? item : item.url;
                      const name = typeof item === 'string' ? `पुरावा ${idx + 1}` : (item.originalName || item.fileName || `पुरावा ${idx + 1}`);
                      const isVideo = item.type === 'video' || item.mimeType?.startsWith('video/') || /\.(mp4|mov|webm|mkv)$/i.test(url);
                      const isPdf = item.type === 'pdf' || item.mimeType === 'application/pdf' || /\.pdf$/i.test(url);
                      const fullUrl = mediaUrl(url);
                      return (
                        <a
                          key={idx}
                          href={fullUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            border: '1px solid #dce4f4',
                            borderRadius: 10,
                            overflow: 'hidden',
                            textDecoration: 'none',
                            background: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                          }}
                        >
                          <div style={{ height: 110, background: '#f4f6fa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                            {isVideo ? (
                              <>
                                <video src={fullUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <span style={{ position: 'absolute', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>▶</span>
                              </>
                            ) : isPdf ? (
                              <span style={{ fontSize: '2rem' }}>📄</span>
                            ) : (
                              <img
                                src={fullUrl}
                                alt={name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text y="18" font-size="18">🖼️</text></svg>';
                                }}
                              />
                            )}
                          </div>
                          <div style={{ padding: '6px 8px', fontSize: '0.74rem', fontWeight: 600, color: '#1a237e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {name}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Citizen Feedback if any */}
              {(statusModal.citizen_rating || statusModal.citizen_feedback) && (
                <div style={{ background: '#fffde7', borderRadius: 10, padding: '14px 18px', marginBottom: 22, border: '1px solid #fff9c4' }}>
                  <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>नागरिक अभिप्राय</div>
                  {statusModal.citizen_rating && <div style={{ fontSize: '1.1rem', marginBottom: 4 }}>{'⭐'.repeat(statusModal.citizen_rating)}{'☆'.repeat(5 - statusModal.citizen_rating)}</div>}
                  {statusModal.citizen_feedback && <div style={{ fontSize: '0.88rem', color: '#555' }}>{statusModal.citizen_feedback}</div>}
                </div>
              )}

              {/* Activity Timeline */}
              {complaintHistory.length > 0 && (
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: '0.82rem', color: '#1a237e', textTransform: 'uppercase', fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 20, height: 20, background: '#1a237e', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>📋</span>
                    कार्यवाही टाइमलाइन ({complaintHistory.length})
                  </div>
                  <div style={{ position: 'relative', paddingLeft: 28 }}>
                    {/* Vertical line */}
                    <div style={{ position: 'absolute', left: 9, top: 4, bottom: 4, width: 2, background: '#e0e0e0' }} />
                    {complaintHistory.map((h, i) => {
                      const actionColors = { created: '#4caf50', status_changed: '#2196f3', due_date_changed: '#ff9800', note_added: '#9c27b0' };
                      const actionIcons = { created: '🆕', status_changed: '🔄', due_date_changed: '📅', note_added: '📝' };
                      const actionLabels = { created: 'तक्रार नोंद', status_changed: 'स्थिती बदल', due_date_changed: 'देय तारीख अद्ययावत', note_added: 'नोंद जोडली' };
                      return (
                        <div key={i} style={{ position: 'relative', marginBottom: i < complaintHistory.length - 1 ? 18 : 0 }}>
                          {/* Timeline dot */}
                          <div style={{ position: 'absolute', left: -23, top: 2, width: 16, height: 16, borderRadius: '50%', background: actionColors[h.action] || '#888', border: '3px solid #fff', boxShadow: '0 0 0 2px ' + (actionColors[h.action] || '#888'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, zIndex: 1 }} />
                          <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '12px 16px', borderLeft: `3px solid ${actionColors[h.action] || '#888'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 6 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{actionIcons[h.action] || '•'}</span>
                                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#333' }}>{actionLabels[h.action] || h.action}</span>
                              </div>
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>{new Date(h.created_at || h.createdAt).toLocaleString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: h.notes || (h.old_value && h.new_value) ? 6 : 0 }}>
                              <strong>{h.changed_by_name}</strong> द्वारे <span style={{ background: '#e8eaf6', padding: '1px 6px', borderRadius: 4, fontSize: '0.72rem', color: '#3949ab' }}>{h.changed_by_role?.replace(/_/g, ' ')}</span>
                            </div>
                            {h.action === 'status_changed' && h.old_value && h.new_value && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                                <span style={{ background: '#ffebee', color: '#c62828', padding: '2px 8px', borderRadius: 6, fontSize: '0.76rem', fontWeight: 600 }}>{h.old_value?.replace(/_/g, ' ')}</span>
                                <span style={{ color: '#999', fontSize: '0.8rem' }}>→</span>
                                <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 6, fontSize: '0.76rem', fontWeight: 600 }}>{h.new_value?.replace(/_/g, ' ')}</span>
                              </div>
                            )}
                            {h.action === 'due_date_changed' && h.new_value && (
                              <div style={{ fontSize: '0.82rem', color: '#e65100', marginTop: 2 }}>📅 नवीन देय तारीख: {new Date(h.new_value).toLocaleDateString('mr-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                            )}
                            {h.notes && <div style={{ fontSize: '0.84rem', color: '#444', marginTop: 6, padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e8e8e8' }}>💬 {h.notes}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Resolution Notes (accumulated) */}
              {statusModal.resolution_notes && (
                <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '14px 18px', marginBottom: 22, border: '1px solid #c8e6c9' }}>
                  <div style={{ fontSize: '0.75rem', color: '#2e7d32', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8 }}>निराकरण नोंदी</div>
                  {statusModal.resolution_notes.split('\n').filter(Boolean).map((note, i) => (
                    <div key={i} style={{ fontSize: '0.84rem', color: '#333', padding: '6px 0', borderBottom: i < statusModal.resolution_notes.split('\n').filter(Boolean).length - 1 ? '1px solid #c8e6c9' : 'none' }}>{note}</div>
                  ))}
                </div>
              )}

              {/* Action Section */}
              <div style={{ background: '#fff8e1', borderRadius: 10, padding: '16px 18px', border: '1px solid #ffe082' }}>
                <div style={{ fontSize: '0.82rem', color: '#f57f17', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>⚡ कार्यवाही करा</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: 4 }}>नोंद जोडा</label>
                    <textarea rows="2" value={statusModal.notes || ''} onChange={e => setStatusModal({...statusModal, notes: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.88rem', boxSizing: 'border-box', resize: 'vertical' }} placeholder="नोंद किंवा अद्ययावत जोडा..." />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: 4 }}>देय तारीख</label>
                    <input type="date" value={statusModal.expected_resolution_date || ''} onChange={e => setStatusModal({...statusModal, expected_resolution_date: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.88rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {getStatusActions(statusModal.status).map(action => (
                    <button key={action.status} className={`btn ${action.cls}`} onClick={() => handleStatusUpdate(action.status)} style={{ borderRadius: 8, fontWeight: 600, fontSize: '0.85rem' }}>{action.label}</button>
                  ))}
                  <button className="btn btn-outline" onClick={() => { setStatusModal(null); setComplaintHistory([]); }} style={{ borderRadius: 8, marginLeft: 'auto' }}>बंद करा</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card complaint-table-card">
            <div className="complaint-table-toolbar">
              <label className="complaint-search"><span>⌕</span><input placeholder="शीर्षक किंवा UID ने शोधा..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} /></label>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व स्थिती</option>
                <option value="filed">दाखल</option>
                <option value="acknowledged">स्वीकृत</option>
                <option value="assigned">नेमणूक</option>
                <option value="in_progress">प्रगतीपथावर</option>
                <option value="resolved">निराकरण</option>
                <option value="verified">सत्यापित</option>
                <option value="closed">बंद</option>
                <option value="reopened">पुन्हा उघडलेली</option>
                <option value="rejected">नाकारलेली</option>
              </select>
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व वर्ग</option><option value="water">पाणी</option><option value="road">रस्ता</option><option value="electricity">वीज</option><option value="sanitation">स्वच्छता</option><option value="health">आरोग्य</option><option value="street_light">पथदिवे</option><option value="encroachment">अतिक्रमण</option><option value="other">इतर</option>
              </select>
              <select value={severityFilter} onChange={e => { setSeverityFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व प्राधान्य</option><option value="low">कमी</option><option value="medium">मध्यम</option><option value="high">उच्च</option><option value="critical">तातडीचे</option>
              </select>
              <button className="complaint-export-btn" onClick={exportCsv} disabled={exporting}>{exporting ? 'निर्यात...' : '⇩ CSV निर्यात'}</button>
            </div>
            <div className="complaint-table-meta"><span><strong>{total}</strong> तक्रार नोंदी</span><span className={`complaint-load-status ${loading ? 'visible' : ''}`}><i /> माहिती अद्ययावत होत आहे</span></div>
            <div className={`table-scroll complaint-table-scroll ${loading ? 'is-refreshing' : ''}`}>
              <table className="data-table complaint-data-table">
                <thead><tr><th><SortHeader column="complaint_uid">UID</SortHeader></th><th><SortHeader column="title">शीर्षक</SortHeader></th><th><SortHeader column="category">वर्ग</SortHeader></th><th><SortHeader column="severity">प्राधान्य</SortHeader></th><th><SortHeader column="status">स्थिती</SortHeader></th><th><SortHeader column="created_at">तारीख</SortHeader></th><th>कृती</th></tr></thead>
                <tbody>
                  {loading ? (
                    <LoadingSpinner.TableRow colSpan={7} message="तक्रार नोंदी लोड होत आहेत..." />
                  ) : sortedComplaints.length === 0 ? (
                    <tr><td colSpan="7" className="complaint-empty">तक्रार नोंदी आढळल्या नाहीत.</td></tr>
                  ) : (
                    sortedComplaints.map((c) => (
                    <tr key={c.id}>
                      <td><span className="complaint-uid">{c.complaint_uid}</span></td>
                      <td><div className="complaint-title-cell"><strong>{c.title}</strong><small>{c.filedByUser?.full_name || 'नागरिक'}</small></div></td>
                      <td><span className="complaint-category">{c.category?.replace(/_/g, ' ')}</span></td>
                      <td><span className={`complaint-severity severity-${c.severity}`}>{c.severity}</span></td>
                      <td><span className={`complaint-status status-${c.status}`}>{c.status?.replace(/_/g, ' ')}</span></td>
                      <td className="complaint-date">{new Date(c.created_at || c.createdAt).toLocaleDateString('mr-IN')}</td>
                      <td><button className="complaint-view-btn" onClick={() => navigate(`${c.id}`)}>{canWriteThis ? 'व्यवस्थापन' : 'तपशील'} →</button></td>
                    </tr>
                  ))
                )}
                </tbody>
              </table>
            </div>
            <div className="table-pagination complaint-pagination">
              <label>प्रति पृष्ठ <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option>10</option><option>20</option><option>50</option></select></label>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>&#8592; मागील</button>
              <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}>पुढील &#8594;</button>
            </div>
      </div>
    </div>
  );
}
